import { Toolkit, type ToolCallMessagePartStatus } from '@assistant-ui/react';
import React from 'react';
import { z } from 'zod';
import { SearchPeriodList } from '../../AiSearch/api/useSearchFilters';
import { searchPeriodToString } from '../../AiSearch/components/IdentifiedList/RefineSearch/FiltersTab/FiltersForm';
import {
    TM_MCP_RESEARCH_COMPETITOR_COMPANIES_TOOL_NAME,
    TM_MCP_RESEARCH_JOB_TITLES_TOOL_NAME,
    TM_MCP_SEARCH_BY_KEYWORDS_TOOL_NAME,
    TM_MCP_SEARCH_SEMANTICALLY_TOOL_NAME,
    TM_MCP_SERVER_NAME,
} from './TMMCPConsts';
import { ToolFallback, ToolFallbackCustomArgs, ToolFallbackCustomResult } from '@/Components/ai-ui/tool-fallback';

import UIStrings from '@/Utils/UIStrings';

interface MCPCompanyAnalysisResultEntry {
    name: string;
    justification: string;
}

interface MCPCompanyAnalysisResult {
    companies: Array<MCPCompanyAnalysisResultEntry>;
}

interface MCPJobTitleAnalysisResultEntry {
    jobTitle: string;
}

interface MCPJobTitleAnalysisResult {
    jobTitles: Array<MCPJobTitleAnalysisResultEntry>;
}

interface MCPKeywordSearchResult {
    addedProfilesCount: number;
}

interface MCPSemanticSearchResult {
    addedProfilesCount: number;
    notFoundInDatabaseCount: number;
}

const searchPeriod = z.enum(SearchPeriodList);

const keywordSearchToolParameters = z.object({
    searchStrategyName: z.string(),
    jobTitles: z.array(z.string()).optional(),
    jobTitleSearchPeriod: searchPeriod.default('Current'),
    locationRadius: z
        .union([z.literal(1), z.literal(15), z.literal(25), z.literal(50), z.literal(75), z.literal(100)])
        .default(25),
    locations: z.array(z.string()).optional(),
    companiesPeriod: searchPeriod.default('Current'),
    companies: z.array(z.string()).optional(),
    keywords: z.array(z.string()).optional(),
    minYearsOfExperience: z.number().int().min(0).default(0),
    maxYearsOfExperience: z.number().int().max(30).default(30),
    includeEmptyExperience: z.boolean().default(false),
    languages: z.array(z.string()).optional(),
});

type KeywordSearchToolArgs = z.infer<typeof keywordSearchToolParameters>;

const semanticSearchToolParameters = z.object({
    prompt: z.string(),
    searchStrategyName: z.string(),
    locations: z.array(z.string()).optional(),
    locationRadius: z
        .union([z.literal(1), z.literal(15), z.literal(25), z.literal(50), z.literal(75), z.literal(100)])
        .default(25),
});

type SemanticSearchToolArgs = z.infer<typeof semanticSearchToolParameters>;

function formatKeywordSearchArgs(args: KeywordSearchToolArgs): React.ReactNode {
    const lines: { label: string; value: string }[] = [];

    if (args.jobTitles?.length) lines.push({ label: UIStrings.JobTitles, value: args.jobTitles.join(', ') });
    if (args.jobTitleSearchPeriod)
        lines.push({
            label: UIStrings.JobTitleSearchPeriod,
            value: searchPeriodToString(args.jobTitleSearchPeriod),
        });
    if (args.locations?.length)
        lines.push({
            label: UIStrings.Locations,
            value: `${args.locations.join(', ')} (${args.locationRadius ?? 25} mi radius)`,
        });
    if (args.companies?.length) lines.push({ label: UIStrings.Companies, value: args.companies.join(', ') });
    if (args.companiesPeriod)
        lines.push({ label: UIStrings.CompanySearchPeriod, value: searchPeriodToString(args.companiesPeriod) });
    if (args.keywords?.length) lines.push({ label: UIStrings.Keywords, value: args.keywords.join(', ') });
    if (args.minYearsOfExperience || args.maxYearsOfExperience !== 30)
        lines.push({
            label: UIStrings.YearsOfExperience,
            value: `${args.minYearsOfExperience ?? 0} – ${args.maxYearsOfExperience ?? 30} years`,
        });
    if (args.includeEmptyExperience) lines.push({ label: UIStrings.IncludeEmptyExperience, value: 'yes' });
    if (args.languages?.length) lines.push({ label: UIStrings.Languages, value: args.languages.join(', ') });

    if (lines.length === 0) return '—';

    return (
        <div className="space-y-2">
            {lines.map(({ label, value }) => (
                <p key={label} className="aui-tool-fallback-result-content">
                    <b>{label}: </b>
                    {value}
                </p>
            ))}
        </div>
    );
}

function formatSemanticSearchArgs(args: SemanticSearchToolArgs): React.ReactNode {
    const lines: { label: string; value: string }[] = [];

    if (args.prompt) lines.push({ label: UIStrings.WebSearchQuery, value: args.prompt });
    if (args.locations?.length)
        lines.push({
            label: UIStrings.Locations,
            value: `${args.locations.join(', ')} (${args.locationRadius ?? 25} mi radius)`,
        });

    if (lines.length === 0) return '—';

    return (
        <div className="space-y-2">
            {lines.map(({ label, value }) => (
                <p key={label} className="aui-tool-fallback-result-content">
                    <b>{label}: </b>
                    {value}
                </p>
            ))}
        </div>
    );
}

function deriveStatus(result: any, isError: boolean | undefined): ToolCallMessagePartStatus {
    // Compute status from result/isError directly — the library's toMessagePartStatus
    // ignores isError and always returns 'complete' when result is present.
    let status: ToolCallMessagePartStatus;

    if (result === undefined) {
        status = { type: 'running' };
    } else if (isError) {
        status = {
            type: 'incomplete',
            reason: 'error',
            error: deriveErrorMessage(result),
        };
    } else {
        status = { type: 'complete' };
    }

    return status;
}

function deriveErrorMessage(result: any): string {
    if (typeof result === 'string') {
        return result;
    } else if ('type' in result && result.type === 'text') {
        return result.text;
    } else {
        return JSON.stringify(result);
    }
}

const tmMCPToolkit: Toolkit = {
    [`${TM_MCP_SERVER_NAME}/${TM_MCP_RESEARCH_JOB_TITLES_TOOL_NAME}`]: {
        description: UIStrings.mcpResearchJobTitlesDescription,
        parameters: {},
        render: ({ result, isError }) => {
            const status: ToolCallMessagePartStatus = deriveStatus(result, isError);

            const castedResult = result !== undefined ? (result as MCPJobTitleAnalysisResult) : undefined;

            return (
                <>
                    <ToolFallback.Root>
                        <ToolFallback.Trigger toolName={UIStrings.mcpResearchJobTitlesToolName} status={status} />
                        <ToolFallback.Content>
                            <ToolFallback.Error status={status} />
                            {!isError && castedResult !== undefined && (
                                <ToolFallbackCustomResult className="border-none">
                                    <p className="aui-tool-fallback-result-content whitespace-pre-wrap">
                                        <b>{UIStrings.JobTitles}: </b>
                                        {castedResult.jobTitles
                                            .map((jt: MCPJobTitleAnalysisResultEntry) => jt.jobTitle)
                                            .join(', ')}
                                    </p>
                                </ToolFallbackCustomResult>
                            )}
                        </ToolFallback.Content>
                    </ToolFallback.Root>
                    {!isError && castedResult !== undefined && (
                        <ToolResultSummary
                            summary={UIStrings.MCPJobTitleAnalysisSummary(
                                castedResult.jobTitles.map((c: MCPJobTitleAnalysisResultEntry) => c.jobTitle),
                            )}
                        />
                    )}
                </>
            );
        },
    },
    [`${TM_MCP_SERVER_NAME}/${TM_MCP_RESEARCH_COMPETITOR_COMPANIES_TOOL_NAME}`]: {
        description: UIStrings.mcpResearchCompetitorDescription,
        parameters: {},
        render: ({ result, isError }) => {
            const status: ToolCallMessagePartStatus = deriveStatus(result, isError);
            const castedResult = result !== undefined ? (result as MCPCompanyAnalysisResult) : undefined;

            return (
                <>
                    <ToolFallback.Root>
                        <ToolFallback.Trigger toolName={UIStrings.mcpResearchCompetitorToolName} status={status} />
                        <ToolFallback.Content>
                            {isError && <ToolFallback.Error status={status} />}
                            {!isError && castedResult !== undefined && (
                                <ToolFallbackCustomResult className="border-none">
                                    <p className="aui-tool-fallback-result-content whitespace-pre-wrap">
                                        {castedResult.companies.map((c: MCPCompanyAnalysisResultEntry) => (
                                            <div key={c.name} className="mb-3">
                                                <p className="mb-1">
                                                    <b>{UIStrings.Company}: </b>
                                                    {c.name}
                                                </p>
                                                <p>
                                                    <b>{UIStrings.Justification}: </b>
                                                    {c.justification}
                                                </p>
                                            </div>
                                        ))}
                                    </p>
                                </ToolFallbackCustomResult>
                            )}
                        </ToolFallback.Content>
                    </ToolFallback.Root>
                    {!isError && castedResult !== undefined && (
                        <ToolResultSummary
                            summary={UIStrings.MCPCompanyAnalysisSummary(
                                castedResult.companies.map((c: MCPCompanyAnalysisResultEntry) => c.name),
                            )}
                        />
                    )}
                </>
            );
        },
    },
    [`${TM_MCP_SERVER_NAME}/${TM_MCP_SEARCH_SEMANTICALLY_TOOL_NAME}`]: {
        description: UIStrings.mcpResearchWebSearch,
        parameters: semanticSearchToolParameters,
        render: ({ result, args, isError }) => {
            const status: ToolCallMessagePartStatus = deriveStatus(result, isError);
            const castedResult = result !== undefined ? (result as MCPSemanticSearchResult) : undefined;

            return (
                <>
                    <ToolFallback.Root>
                        <ToolFallback.Trigger
                            toolName={`${UIStrings.mcpResearchWebSearchToolName} ${args?.searchStrategyName ? ` - ${args.searchStrategyName}` : ''}`}
                            status={status}
                        />
                        <ToolFallback.Content>
                            {isError && <ToolFallback.Error status={status} />}
                            <ToolFallbackCustomArgs>
                                {formatSemanticSearchArgs(args as SemanticSearchToolArgs)}
                            </ToolFallbackCustomArgs>
                        </ToolFallback.Content>
                    </ToolFallback.Root>
                    {!isError && castedResult !== undefined && (
                        <ToolResultSummary
                            summary={UIStrings.MCPSearchCandidateResultSummary(castedResult.addedProfilesCount)}
                        />
                    )}
                </>
            );
        },
    },
    [`${TM_MCP_SERVER_NAME}/${TM_MCP_SEARCH_BY_KEYWORDS_TOOL_NAME}`]: {
        description: UIStrings.mcpResearchDatabaseSearch,
        parameters: keywordSearchToolParameters,
        render: ({ result, args, isError }) => {
            const status: ToolCallMessagePartStatus = deriveStatus(result, isError);
            const castedResult = result !== undefined ? (result as MCPKeywordSearchResult) : undefined;

            return (
                <>
                    <ToolFallback.Root>
                        <ToolFallback.Trigger
                            toolName={`${UIStrings.mcpResearchDatabaseSearchToolName} ${args?.searchStrategyName ? ` - ${args.searchStrategyName}` : ''}`}
                            status={status}
                        />
                        <ToolFallback.Content>
                            {isError && <ToolFallback.Error status={status} />}
                            <ToolFallbackCustomArgs>
                                {formatKeywordSearchArgs(args as KeywordSearchToolArgs)}
                            </ToolFallbackCustomArgs>
                        </ToolFallback.Content>
                    </ToolFallback.Root>
                    {!isError && castedResult !== undefined && (
                        <ToolResultSummary
                            summary={UIStrings.MCPSearchCandidateResultSummary(castedResult.addedProfilesCount)}
                        />
                    )}
                </>
            );
        },
    },
};

interface ToolOutboxResultProps {
    summary?: string;
}

function ToolResultSummary({ summary }: ToolOutboxResultProps) {
    if (summary === undefined) return null;

    return (
        <div className="my-4">
            {summary && <p className="aui-tool-fallback-result-header mb-2 font-semibold">{summary}</p>}
        </div>
    );
}

export default tmMCPToolkit;
